// apps/cli/src/lib/github-app-permissions.ts
//
// Assert, up front, that the GitHub App INSTALLATION actually grants what the
// brain flow needs — before anything is created.
//
// Why this is not paranoia. `buildManifest` requests the correct permission set
// today, so a founder who runs the manifest flow gets a correct App. But GitHub
// does not apply a permission upgrade to an EXISTING installation until an org
// owner approves it, so an App whose manifest was fixed can still present the old
// set to the API. When that happens the failure surfaces as a bare
//
//   POST /orgs/<org>/repos → 403 "Resource not accessible by integration"
//
// seven phases into a cloud install, with nothing in the message naming the App,
// the permission, or who can fix it. That is the exact shape of the 2026-07-26
// smoke failure. Reading the granted set costs one request and turns it into a
// named remedy at phase zero.

import { signAppJwt } from "@m8t-stack/github-app-auth";
import { LocalCliError } from "./errors.js";

/**
 * What the brain flow actually exercises, and the operation that needs each.
 * Kept aligned with `buildManifest`'s `default_permissions` — that function
 * declares what we ASK for; this declares what we cannot work without.
 */
export const REQUIRED_INSTALLATION_PERMISSIONS = [
  { key: "administration", level: "write", why: "create the brain repository (POST /orgs/<org>/repos)" },
  { key: "contents", level: "write", why: "push the brain template and commit memory updates" },
  { key: "workflows", level: "write", why: "push the brain template's .github/workflows/ files" },
  { key: "metadata", level: "read", why: "read repository metadata" },
] as const;

/**
 * GitHub's permission levels, weakest first. `admin` satisfies `write` satisfies
 * `read`. Typed with `| undefined` deliberately: the keys come off the wire, so a
 * level GitHub adds later reads as unranked (0) — treated as insufficient — rather
 * than as `NaN >= n`, which is false but for the wrong reason.
 */
const LEVEL_RANK: Record<string, number | undefined> = { read: 1, write: 2, admin: 3 };

export interface InstallationPermissionGap {
  key: string;
  required: string;
  /** What the installation actually grants, or null when the permission is absent entirely. */
  granted: string | null;
  why: string;
}

/** Pure: compare a granted permission map against the required set. */
export function findPermissionGaps(granted: Record<string, string | undefined>): InstallationPermissionGap[] {
  return REQUIRED_INSTALLATION_PERMISSIONS.flatMap((req) => {
    const have = granted[req.key];
    const haveRank = have ? (LEVEL_RANK[have] ?? 0) : 0;
    const needRank = LEVEL_RANK[req.level] ?? 0;
    return haveRank >= needRank ? [] : [{ key: req.key, required: req.level, granted: have ?? null, why: req.why }];
  });
}

/** Pure: the refusal a founder (or an installer log) has to be able to act on. */
export function buildPermissionRefusal(args: { org: string; slug?: string; gaps: InstallationPermissionGap[] }): {
  message: string;
  hint: string;
} {
  const lines = args.gaps.map((g) => `  • ${g.key}: need ${g.required}, installation grants ${g.granted ?? "nothing"} — needed to ${g.why}`);
  const appLabel = args.slug ? `'${args.slug}'` : "the m8t brain App";
  return {
    message:
      `The GitHub App installed on '${args.org}' is missing permissions the brain flow needs:\n${lines.join("\n")}`,
    hint:
      `An App's permissions are not applied to an installation until an org owner approves them, so editing the App is not enough. ` +
      `Either approve the pending request at https://github.com/organizations/${args.org}/settings/installations (open ${appLabel} → Review request), ` +
      `or create a fresh App with the right set: m8t brain app-create --org ${args.org}`,
  };
}

export interface InstallationPermissionCheck {
  /** The permissions the installation was actually granted, or null if unreadable. Values are
   *  typed optional because this map comes off the wire: a key we ask about may simply not be there. */
  granted: Record<string, string | undefined> | null;
  slug?: string;
  /** Set when the read itself failed — a transient signal, never a reason to refuse. */
  unreadable?: string;
}

/** Read the installation's GRANTED permissions. Collapses every failure to `unreadable`. */
export async function readInstallationPermissions(args: {
  appId: string;
  privateKeyPem: string;
  installationId: string;
  fetchImpl?: typeof fetch;
}): Promise<InstallationPermissionCheck> {
  const doFetch = args.fetchImpl ?? fetch;
  let jwt: string;
  try {
    jwt = signAppJwt({ appId: args.appId, privateKeyPem: args.privateKeyPem });
  } catch (e) {
    return { granted: null, unreadable: `could not sign an App JWT: ${(e as Error).message}` };
  }
  try {
    const res = await doFetch(`https://api.github.com/app/installations/${args.installationId}`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
        "User-Agent": "m8t",
      },
    });
    if (!res.ok) return { granted: null, unreadable: `GET /app/installations/${args.installationId} → HTTP ${String(res.status)}` };
    const body = (await res.json()) as { permissions?: Record<string, string>; app_slug?: string };
    if (!body.permissions || typeof body.permissions !== "object") {
      return { granted: null, unreadable: "the installation response carried no permissions object" };
    }
    return { granted: body.permissions, ...(body.app_slug ? { slug: body.app_slug } : {}) };
  } catch (e) {
    return { granted: null, unreadable: (e as Error).message };
  }
}

/**
 * Refuse early when the installation cannot do the job.
 *
 * Fails CLOSED only on a deterministic answer — a successful read that is missing
 * a permission. Everything else (an unreachable api.github.com, a rate limit, a
 * malformed body) resolves to `onUnverified` and continues, because a gate may
 * fail closed only on a signal that cannot fail transiently. This one can, and a
 * network blip must never be the reason a founder's install refuses to start.
 * The same reasoning `bootstrap launch` applies to findValidBrainApp.
 */
export async function assertInstallationPermissions(args: {
  appId: string;
  privateKeyPem: string;
  installationId: string;
  org: string;
  fetchImpl?: typeof fetch;
  onUnverified?: (message: string) => void;
}): Promise<void> {
  const check = await readInstallationPermissions(args);
  if (!check.granted) {
    args.onUnverified?.(`could not confirm the GitHub App's granted permissions (${check.unreadable ?? "unknown"}) — continuing`);
    return;
  }
  const gaps = findPermissionGaps(check.granted);
  if (gaps.length === 0) return;
  const { message, hint } = buildPermissionRefusal({ org: args.org, ...(check.slug ? { slug: check.slug } : {}), gaps });
  throw new LocalCliError({ code: "GITHUB_APP_PERMISSIONS_INSUFFICIENT", message, hint });
}
